import { ListingTypeBadge } from "@/components/shared/listing-type-badge"
import { Results } from "../search-layout"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { createAttributeOptions } from "@/constants/speciality"
import { cn } from "@/lib/utils"
import { typedEntries, typedValues } from "@/types/helpers"
import { TagHierarchy } from "@/types/tag-hierarchy"
import type {
  ListingProfileListingType,
  SearchEducatorsEducatorDocument,
} from "@/types/serializers"
import { SearchDetailLink } from "@/components/search/search-return-navigation"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

export type EducatorSearchHit = Omit<
  SearchEducatorsEducatorDocument,
  "listing_type" | "name" | "speciality_attributes" | "tags"
> & {
  id: string
  listing_type: ListingProfileListingType
  name: string
  speciality_attributes: string[]
  tags: string[]
}

export function EducatorSearchResults<Result extends EducatorSearchHit>({
  ResultComponent,
}: {
  ResultComponent: React.ComponentType<{ result: Result }>
}) {
  const { t } = useTranslation(["search"])
  return (
    <Results
      empty={
        <div
          className="flex h-full flex-col items-center justify-center"
          data-testid="search-results-empty"
        >
          <h2 className="mb-2 text-2xl font-bold tracking-tight">
            {t("result.educators.empty.label")}
          </h2>
          <p className="mb-8 max-w-md">
            {t("result.educators.empty.description")}
          </p>
        </div>
      }
      ResultComponent={ResultComponent}
    />
  )
}

export function EducatorResultCard({
  result,
  children,
}: {
  result: EducatorSearchHit
  children: React.ReactNode
}) {
  return (
    <li className="h-full">
      <SearchDetailLink
        href={`/search/educators/${result.id}`}
        aria-label={result.name}
        role="link"
        className="block h-full"
      >
        <Card hoverable className="group h-full">
          {children}
        </Card>
      </SearchDetailLink>
    </li>
  )
}

export function EducatorShortlistResultCard({
  result,
  children,
}: {
  result: EducatorSearchHit
  children: React.ReactNode
}) {
  return (
    <li className="h-full" aria-label={result.name}>
      <Card className="h-full">{children}</Card>
    </li>
  )
}

export function EducatorResultFooter({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <CardFooter className="flex flex-row flex-wrap gap-4 pt-4">
      {children}
    </CardFooter>
  )
}

export function EducatorResultHeader({
  result,
}: {
  result: EducatorSearchHit
}) {
  return (
    <CardHeader className="p-0 pt-2">
      <CardTitle className="flex flex-row items-center gap-4">
        <Avatar>
          <AvatarFallback className="text-lg">{result.name[0]}</AvatarFallback>
        </Avatar>
        <div className="group-hover:text-primary text-lg">{result.name}</div>
        <ListingTypeBadge listingType={result.listing_type} />
      </CardTitle>
    </CardHeader>
  )
}

export function EducatorResultContent({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <CardContent className="flex flex-col gap-4 pb-0 pl-12">
      {children}
    </CardContent>
  )
}

export function EducatorResultSpecialities({
  result,
}: {
  result: EducatorSearchHit
}) {
  const educatorSpecialities = useMemo(() => {
    return createAttributeOptions("search:select_attributes")
      .filter((attr) => result.speciality_attributes.includes(attr.value))
      .map((item) => item.label)
  }, [result])

  const { t } = useTranslation("search")
  return (
    <ItemsDisplay
      badgeClassName="bg-brand-light-grey"
      items={educatorSpecialities}
      label={t("result.specialities")}
    />
  )
}

export function EducatorResultTags({ result }: { result: EducatorSearchHit }) {
  const { t: tTags } = useTranslation("listing_profile_tags")
  const educatorTags: [string, string[]][] = useMemo(() => {
    const tagHierarchy = tTags("tag_categories", {
      returnObjects: true,
    }) as TagHierarchy
    const recordTagSet = new Set<string>(result.tags)
    return typedValues(tagHierarchy).map(({ name, tag_subcategories }) => [
      name,
      typedValues(tag_subcategories).flatMap(({ tags }) =>
        typedEntries(tags).flatMap(([tagKey, tag]) =>
          recordTagSet.has(tagKey) ? tag.name : [],
        ),
      ),
    ])
  }, [result, tTags])

  const { t } = useTranslation("search")
  return (
    <div className="contents">
      <span className="text-sm font-medium">{t("result.tags")}</span>
      <div className="grid grid-cols-2 gap-x-2 gap-y-4">
        {educatorTags.map(([categoryName, tags], index) => (
          <ItemsDisplay
            items={tags}
            label={categoryName}
            key={index}
            labelClassName="text-xs"
            display={2}
          />
        ))}
      </div>
    </div>
  )
}

function ItemsDisplay({
  items,
  label,
  labelClassName,
  badgeClassName,
  display = 4,
}: {
  items: string[]
  label: string
  labelClassName?: string
  display?: number
  badgeClassName?: string
}) {
  const displayedItems = items.slice(0, display)
  const remainingItems = items.length - displayedItems.length
  const { t } = useTranslation("search", { keyPrefix: "result" })
  if (items.length == 0) return null

  return (
    <div className="flex flex-col gap-2">
      <span className={cn("text-sm font-medium", labelClassName)}>{label}</span>
      <div className={"flex flex-wrap gap-4 overflow-hidden"}>
        {displayedItems.map((item) => (
          <Badge
            className={cn("bg-brand-light-philo-mustard", badgeClassName)}
            key={item}
          >
            {item}
          </Badge>
        ))}
        {remainingItems > 0 && (
          <Badge>{t("more", { count: remainingItems })}</Badge>
        )}
      </div>
    </div>
  )
}
